/**
 * useSubscription Hook
 * Centralized subscription/tier logic for the entire application.
 * 
 * TIER HIERARCHY (matches pricing page):
 * - ADMIN: Full access to everything including admin features
 * - PREMIUM (monthly/lifetime): Unlimited portfolios/instruments, all backtest/rebalance modes
 * - BOOK_BUNDLE: 1 portfolio (15 instruments), buy_hold backtest, sector_based rebalance
 * - FREE: 1 portfolio (5 instruments), NO backtest/rebalance
 * 
 * Usage:
 *   const { isPremium, canUseBacktestMode, permissions } = useSubscription(user);
 */

export const TIERS = {
  ADMIN: 'admin',
  PREMIUM_LIFETIME: 'premium_lifetime',
  PREMIUM_MONTHLY: 'premium_monthly',
  PREMIUM: 'premium',
  BOOK_BUNDLE: 'book_bundle',
  FREE: 'free'
};

// Backtest modes by tier
export const BACKTEST_MODES = {
  [TIERS.FREE]: [],
  [TIERS.BOOK_BUNDLE]: ['buy_hold'],
  [TIERS.PREMIUM]: ['buy_hold', 'performance_based', 'adaptive_momentum'],
  [TIERS.PREMIUM_MONTHLY]: ['buy_hold', 'performance_based', 'adaptive_momentum'],
  [TIERS.PREMIUM_LIFETIME]: ['buy_hold', 'performance_based', 'adaptive_momentum'],
  [TIERS.ADMIN]: ['buy_hold', 'performance_based', 'adaptive_momentum']
};

// Rebalance strategies by tier
export const REBALANCE_MODES = {
  [TIERS.FREE]: [],
  [TIERS.BOOK_BUNDLE]: ['sector_based'],
  [TIERS.PREMIUM]: ['sector_based', 'performance_based', 'adaptive_momentum'],
  [TIERS.PREMIUM_MONTHLY]: ['sector_based', 'performance_based', 'adaptive_momentum'],
  [TIERS.PREMIUM_LIFETIME]: ['sector_based', 'performance_based', 'adaptive_momentum'],
  [TIERS.ADMIN]: ['sector_based', 'performance_based', 'adaptive_momentum']
};

// Portfolio limits by tier
export const PORTFOLIO_LIMITS = {
  [TIERS.FREE]: 1,
  [TIERS.BOOK_BUNDLE]: 1,
  [TIERS.PREMIUM]: 999,
  [TIERS.PREMIUM_MONTHLY]: 999,
  [TIERS.PREMIUM_LIFETIME]: 999,
  [TIERS.ADMIN]: 999
};

// Instrument limits by tier
export const INSTRUMENTS_LIMITS = {
  [TIERS.FREE]: 5,
  [TIERS.BOOK_BUNDLE]: 15,
  [TIERS.PREMIUM]: 9999,
  [TIERS.PREMIUM_MONTHLY]: 9999,
  [TIERS.PREMIUM_LIFETIME]: 9999,
  [TIERS.ADMIN]: 9999
};

// Admin emails that always get full access
const ADMIN_EMAILS = ['cepedafa662@gmail.com'];

// Pricing page URL for upgrade CTAs
export const PRICING_URL = '/pricing';

// Feature access matrix
const FEATURE_ACCESS = {
  // FREE: Basic features
  investor_profile: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE, TIERS.FREE],
  risk_assessment: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE, TIERS.FREE],
  cycle_analysis: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE, TIERS.FREE],
  market_analysis: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE, TIERS.FREE],
  create_portfolio: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE, TIERS.FREE],
  
  // BOOK_BUNDLE: Basic backtesting & rebalancing
  basic_backtesting: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE],
  basic_rebalancing: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE],
  basic_correlation: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE],
  compare_instruments: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE],
  
  // PREMIUM: Advanced features
  advanced_backtesting: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  advanced_rebalancing: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  correlation_heatmap: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  instrument_evaluator: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  guidance: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  alerts: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  sector_winners: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM, TIERS.BOOK_BUNDLE],
  dividend_analysis: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  export_data: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  auto_invest: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  unlimited_portfolios: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  unlimited_instruments: [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM],
  
  // ADMIN ONLY
  admin_dashboard: [TIERS.ADMIN],
};

/**
 * Get the effective tier for a user
 */
export function getEffectiveTier(user) {
  if (!user) return TIERS.FREE;
  
  // Check if user is admin by email or flag
  const isAdminByEmail = ADMIN_EMAILS.includes(user.email?.toLowerCase());
  const isAdminByFlag = user.is_admin === true || user.role === 'admin';
  
  if (isAdminByEmail || isAdminByFlag) {
    return TIERS.ADMIN;
  }
  
  // Normalize subscription field
  const subscription = user.subscription || user.subscription_type || user.effective_tier || 'free';
  
  // Map subscription to tier
  switch (subscription.toLowerCase()) {
    case 'premium_lifetime':
      return TIERS.PREMIUM_LIFETIME;
    case 'premium_monthly':
    case 'premium':
      return TIERS.PREMIUM_MONTHLY;
    case 'book_bundle':
      return TIERS.BOOK_BUNDLE;
    default:
      return TIERS.FREE;
  }
}

/**
 * Check if user has access to a specific feature
 */
export function canAccessFeature(user, feature) {
  const tier = getEffectiveTier(user);
  if (tier === TIERS.ADMIN) return true;
  
  const allowedTiers = FEATURE_ACCESS[feature];
  if (!allowedTiers) return false;
  
  return allowedTiers.includes(tier);
}

/**
 * Check if user can use a specific backtest mode
 */
export function canUseBacktestMode(user, mode) {
  const tier = getEffectiveTier(user);
  const allowedModes = BACKTEST_MODES[tier] || [];
  return allowedModes.includes(mode);
}

/**
 * Check if user can use a specific rebalance strategy
 */
export function canUseRebalanceMode(user, strategy) {
  const tier = getEffectiveTier(user);
  const allowedModes = REBALANCE_MODES[tier] || [];
  return allowedModes.includes(strategy);
}

/**
 * Get upgrade message for a locked feature
 */
export function getUpgradeMessage(feature, currentTier) {
  const messages = {
    backtesting: {
      [TIERS.FREE]: { text: 'Unlock Backtesting', upgradeTo: 'Book Bundle' },
      [TIERS.BOOK_BUNDLE]: { text: 'Unlock Advanced Backtesting', upgradeTo: 'Premium' }
    },
    rebalancing: {
      [TIERS.FREE]: { text: 'Unlock Rebalancing', upgradeTo: 'Book Bundle' },
      [TIERS.BOOK_BUNDLE]: { text: 'Unlock Advanced Rebalancing', upgradeTo: 'Premium' }
    },
    correlation_heatmap: {
      [TIERS.FREE]: { text: 'Unlock Correlation Analysis', upgradeTo: 'Book Bundle' },
      [TIERS.BOOK_BUNDLE]: { text: 'Unlock Advanced Heatmaps', upgradeTo: 'Premium' }
    },
    performance_based: {
      [TIERS.FREE]: { text: 'Unlock Performance Based', upgradeTo: 'Premium' },
      [TIERS.BOOK_BUNDLE]: { text: 'Unlock Performance Based', upgradeTo: 'Premium' }
    },
    adaptive_momentum: {
      [TIERS.FREE]: { text: 'Unlock Adaptive Momentum', upgradeTo: 'Premium' },
      [TIERS.BOOK_BUNDLE]: { text: 'Unlock Adaptive Momentum', upgradeTo: 'Premium' }
    }
  };
  
  return messages[feature]?.[currentTier] || { text: 'Upgrade Required', upgradeTo: 'Premium' };
}

/**
 * Main hook for subscription logic
 */
export function useSubscription(user) {
  const tier = getEffectiveTier(user);
  
  const isAdmin = tier === TIERS.ADMIN;
  const isPremium = [TIERS.ADMIN, TIERS.PREMIUM_LIFETIME, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM].includes(tier);
  const isBookBundle = tier === TIERS.BOOK_BUNDLE;
  const isFree = tier === TIERS.FREE;
  
  // Display name for the tier
  const tierDisplayName = {
    [TIERS.ADMIN]: 'Administrator',
    [TIERS.PREMIUM_LIFETIME]: 'Premium Lifetime',
    [TIERS.PREMIUM_MONTHLY]: 'Premium',
    [TIERS.PREMIUM]: 'Premium',
    [TIERS.BOOK_BUNDLE]: 'Book Bundle',
    [TIERS.FREE]: 'Free'
  }[tier] || 'Free';
  
  // Limits based on tier
  const limits = {
    maxPortfolios: PORTFOLIO_LIMITS[tier] || 1,
    maxInstrumentsPerPortfolio: INSTRUMENTS_LIMITS[tier] || 5,
    maxComparisons: isPremium || isAdmin ? -1 : 2,
  };
  
  // Permissions object (comprehensive)
  const permissions = {
    // Limits
    maxPortfolios: limits.maxPortfolios,
    maxInstruments: limits.maxInstrumentsPerPortfolio,
    
    // Backtest permissions
    backtestEnabled: isBookBundle || isPremium || isAdmin,
    backtestModes: BACKTEST_MODES[tier] || [],
    canUseBuyHold: canUseBacktestMode(user, 'buy_hold'),
    canUsePerformanceBased: canUseBacktestMode(user, 'performance_based'),
    canUseAdaptiveMomentum: canUseBacktestMode(user, 'adaptive_momentum'),
    
    // Rebalance permissions
    rebalancingEnabled: isBookBundle || isPremium || isAdmin,
    rebalanceModes: REBALANCE_MODES[tier] || [],
    canUseSectorBased: canUseRebalanceMode(user, 'sector_based'),
    canUsePerformanceRebalance: canUseRebalanceMode(user, 'performance_based'),
    canUseAdaptiveRebalance: canUseRebalanceMode(user, 'adaptive_momentum'),
    
    // Correlation permissions
    correlationEnabled: isBookBundle || isPremium || isAdmin,
    correlationHeatmap: isPremium || isAdmin,
    
    // Other features
    exportEnabled: isPremium || isAdmin,
    autoInvest: isPremium || isAdmin,
    alertsEnabled: isPremium || isAdmin,
    guidanceEnabled: isPremium || isAdmin,
    evaluatorEnabled: isPremium || isAdmin,
    sectorWinnersEnabled: isPremium || isBookBundle || isAdmin,
    dividendAnalysis: isPremium || isAdmin,
  };
  
  return {
    tier,
    tierDisplayName,
    isAdmin,
    isPremium,
    isBookBundle,
    isFree,
    limits,
    permissions,
    
    // Feature access
    canAccess: (feature) => canAccessFeature(user, feature),
    canUseBacktestMode: (mode) => canUseBacktestMode(user, mode),
    canUseRebalanceMode: (strategy) => canUseRebalanceMode(user, strategy),
    getUpgradeMessage: (feature) => getUpgradeMessage(feature, tier),
    
    // Convenience methods
    canCreatePortfolio: true, // All tiers can create (with limits)
    canUseEvaluator: canAccessFeature(user, 'instrument_evaluator'),
    canUseCorrelation: canAccessFeature(user, 'basic_correlation'),
    canUseCorrelationHeatmap: canAccessFeature(user, 'correlation_heatmap'),
    canUseBasicBacktesting: canAccessFeature(user, 'basic_backtesting'),
    canUseAdvancedBacktesting: canAccessFeature(user, 'advanced_backtesting'),
    canUseBasicRebalancing: canAccessFeature(user, 'basic_rebalancing'),
    canUseAdvancedRebalancing: canAccessFeature(user, 'advanced_rebalancing'),
    canUseGuidance: canAccessFeature(user, 'guidance'),
    canUseAlerts: canAccessFeature(user, 'alerts'),
    canExport: canAccessFeature(user, 'export_data'),
    canAccessAdmin: canAccessFeature(user, 'admin_dashboard'),
    canSeeDividends: canAccessFeature(user, 'dividend_analysis'),
    canSeeSectorWinners: canAccessFeature(user, 'sector_winners'),
  };
}

export default useSubscription;
